import { useState, useEffect } from 'react';
import { X, User, Building, FileText, MessageSquare, Loader2, Save } from 'lucide-react';
import { CategoryBadge } from './CategoryBadge';
import { AlertDetail } from './AlertBadge';
import type {
  OversightConversationDetail,
  OversightMessage,
  MessageSource,
  ChatCategory,
  ReviewStatus,
  UpdateReviewRequest,
} from '../../types';
import { REVIEW_STATUS_INFO } from '../../types';
import { fieldCls } from '../../../ui';

interface ReviewPanelProps {
  conversation: OversightConversationDetail | null;
  categories: ChatCategory[];
  loading: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (conversationId: string, review: UpdateReviewRequest) => Promise<void>;
  onDismissAlert: (alertId: string) => Promise<void>;
}

/**
 * Groups source entries by document, deduplicating and collecting page numbers.
 */
function groupSourcesByDocument(sources: MessageSource[]): Array<{
  document_id: string;
  filename: string;
  document_type: string | null;
  pages: number[];
}> {
  const map = new Map<string, { document_id: string; filename: string; document_type: string | null; pages: Set<number> }>();
  for (const s of sources) {
    const existing = map.get(s.document_id);
    if (existing) {
      if (s.page_number != null) existing.pages.add(s.page_number);
    } else {
      const pages = new Set<number>();
      if (s.page_number != null) pages.add(s.page_number);
      map.set(s.document_id, { document_id: s.document_id, filename: s.filename, document_type: s.document_type, pages });
    }
  }
  return Array.from(map.values()).map(d => ({
    ...d,
    pages: Array.from(d.pages).sort((a, b) => a - b),
  }));
}

const REVIEW_STATUS_OPTIONS: { value: ReviewStatus; label: string }[] = [
  { value: 'pending', label: 'Pending Review' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'flagged', label: 'Flagged for Follow-up' },
  { value: 'escalated', label: 'Escalated' },
];

export function ReviewPanel({
  conversation,
  categories,
  loading,
  saving,
  onClose,
  onSave,
  onDismissAlert,
}: ReviewPanelProps) {
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus>('pending');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Reset form when conversation changes
  useEffect(() => {
    if (conversation?.review) {
      setReviewStatus(conversation.review.review_status);
      setSelectedCategories(conversation.review.categories || []);
      setNotes(conversation.review.notes || '');
      setHasChanges(false);
    } else {
      setReviewStatus('pending');
      setSelectedCategories([]);
      setNotes('');
      setHasChanges(false);
    }
  }, [conversation]);

  const handleStatusChange = (status: ReviewStatus) => {
    setReviewStatus(status);
    setHasChanges(true);
  };

  const handleToggleCategory = (categoryName: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryName)
        ? prev.filter((c) => c !== categoryName)
        : [...prev, categoryName]
    );
    setHasChanges(true);
  };

  const handleNotesChange = (value: string) => {
    setNotes(value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!conversation) return;
    await onSave(conversation.id, {
      review_status: reviewStatus,
      categories: selectedCategories,
      notes: notes || undefined,
    });
    setHasChanges(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-xl border-l border-gray-200 flex flex-col z-50 dark:bg-gray-800 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Review Conversation</h2>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 rounded-lg transition-colors duration-150 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:text-gray-200 dark:hover:bg-gray-700"
        >
          <X size={20} />
        </button>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 size={32} className="animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : conversation ? (
        <>
          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Metadata */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <User size={16} className="text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-300">
                    {conversation.users?.full_name || 'Unknown'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Building size={16} className="text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-300">
                    {conversation.sites?.name || 'No site'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FileText size={16} className="text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-300">
                    {conversation.protocols?.protocol_number || 'No protocol'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MessageSquare size={16} className="text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-300">
                    {conversation.messages.length} messages
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 dark:text-gray-400">
                Started {formatDate(conversation.created_at)}
              </p>
            </div>

            {/* Alerts Section */}
            {conversation.alerts.length > 0 && (
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 mb-3 dark:text-gray-100">Alerts</h3>
                <div className="space-y-2">
                  {conversation.alerts.map((alert) => (
                    <AlertDetail
                      key={alert.id}
                      type={alert.alert_type}
                      severity={alert.severity}
                      details={alert.details}
                      dismissed={alert.is_dismissed}
                      onDismiss={
                        !alert.is_dismissed
                          ? () => onDismissAlert(alert.id)
                          : undefined
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 mb-3 dark:text-gray-100">Conversation</h3>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {conversation.messages.map((message) => {
                  const groupedSources = message.role === 'assistant' && message.sources?.length
                    ? groupSourcesByDocument(message.sources)
                    : [];
                  return (
                    <div
                      key={message.id}
                      className={`rounded-lg p-3 ${
                        message.role === 'user'
                          ? 'bg-primary-50 ml-8 dark:bg-primary-900/20'
                          : message.role === 'assistant'
                          ? 'bg-gray-50 mr-8 dark:bg-gray-700/50'
                          : 'bg-primary-50 text-center text-sm dark:bg-primary-900/20'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500 capitalize dark:text-gray-400">
                            {message.role}
                          </span>
                          {message.role === 'assistant' && conversation.protocols?.protocol_number && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                              {conversation.protocols.protocol_number}
                            </span>
                          )}
                        </div>
                        {message.role === 'assistant' && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {(message.input_tokens || 0) + (message.output_tokens || 0)} tokens
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap dark:text-gray-200">
                        {message.content}
                      </p>
                      {groupedSources.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 space-y-1 dark:border-gray-600">
                          {groupedSources.map((doc) => (
                            <div key={doc.document_id} className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                              <FileText size={12} className="text-gray-400 flex-shrink-0" />
                              <span className="truncate font-medium">{doc.filename}</span>
                              {doc.pages.length > 0 && (
                                <span className="text-gray-400 flex-shrink-0 dark:text-gray-500">
                                  (pg {doc.pages.join(', ')})
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                        {formatDate(message.created_at)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Review Form */}
            <div className="px-6 py-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3 dark:text-gray-100">Review</h3>

              {/* Status Selection */}
              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-2 dark:text-gray-400">Status</label>
                <div className="flex flex-wrap gap-2">
                  {REVIEW_STATUS_OPTIONS.map((option) => {
                    const info = REVIEW_STATUS_INFO[option.value];
                    const isSelected = reviewStatus === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleStatusChange(option.value)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          isSelected
                            ? `${info.bgColor} ${info.color} ring-2 ring-offset-1 dark:ring-offset-gray-800 ${option.value === 'pending' ? 'ring-gray-400' : option.value === 'reviewed' ? 'ring-green-400' : option.value === 'flagged' ? 'ring-amber-400' : 'ring-red-400'}`
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category Selection */}
              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-2 dark:text-gray-400">Categories</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => {
                    const isSelected = selectedCategories.includes(category.name);
                    return (
                      <button
                        key={category.id}
                        onClick={() => handleToggleCategory(category.name)}
                        className={`rounded-full transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          isSelected ? 'ring-2 ring-offset-1 ring-primary-400 dark:ring-offset-gray-800' : 'opacity-60 hover:opacity-100'
                        }`}
                      >
                        <CategoryBadge name={category.name} color={category.color} />
                      </button>
                    );
                  })}
                </div>
                {selectedCategories.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Selected:</span>
                    {selectedCategories.map((name) => {
                      const category = categories.find((c) => c.name === name);
                      return (
                        <CategoryBadge
                          key={name}
                          name={name}
                          color={category?.color}
                          small
                          onRemove={() => handleToggleCategory(name)}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm text-gray-600 mb-2 dark:text-gray-400">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => handleNotesChange(e.target.value)}
                  placeholder="Add review notes..."
                  rows={3}
                  className={`w-full ${fieldCls}`}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Review
                </>
              )}
            </button>
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
          Select a conversation to review
        </div>
      )}
    </div>
  );
}
